/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import {
  ApiHolder,
  createApiRef,
  DiscoveryApi,
  FetchApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';

import {
  Assessment,
  Participant,
  Event,
  IdeaComment,
  IdeaLikes,
  ListEventFilter,
  EventConfigValue,
} from './types';
import { getLocalDevUserHeaders } from './util/localDevUser';

export const tomtomLabApiRef = createApiRef<TomtomLabApi>({
  id: 'tomtom-lab',
});

export interface TomtomLabApi {
  updateIdea(idea: any): Promise<any>;

  registerIdea(idea: any): Promise<any>;

  getIdeaById(id: number): Promise<any>;

  getIdeasByMemberUserRef(userRef: string): Promise<any>;

  getMembers(id: number): Promise<any>;

  deleteMember(id: number, userId: string): Promise<void>;

  addMember(id: number, userId: string): Promise<any>;

  getIdeas(limit?: number, order?: string, eventId?: number): Promise<any>;

  deleteIdea(id: number): Promise<void>;

  submitSolution(solution: any): Promise<any>;

  updateAssessment(assessment: Assessment): Promise<any>;

  createAssessment(assessment: Assessment): Promise<any>;

  getSelfAssessment(ideaId: number): Promise<any>;

  getJuryAssessment(ideaId: number, userRef: String): Promise<any>;

  getJuryAssessmentsForIdea(ideaId: number): Promise<Assessment[]>;

  /**
   * Idea engagement. The calling user is resolved server-side, so none of these
   * take a userRef.
   */
  likeIdea(ideaId: number): Promise<IdeaLikes>;

  unlikeIdea(ideaId: number): Promise<IdeaLikes>;

  getIdeaComments(ideaId: number): Promise<IdeaComment[]>;

  addIdeaComment(ideaId: number, comment: string): Promise<IdeaComment>;

  updateIdeaComment(commentId: number, comment: string): Promise<IdeaComment>;

  deleteIdeaComment(commentId: number): Promise<void>;

  registerParticipant(participant: Participant): Promise<any>;

  getActiveEventParticipants(): Promise<ReadonlyArray<Participant>>;

  getActiveEventParticipant(
    userRef: string,
  ): Promise<Readonly<Participant> | null>;

  updateParticipant(participant: Participant): Promise<{ status: string }>;

  getEvents(filter: ListEventFilter): Promise<Event[]>;

  getEventById(id: number): Promise<Event | null>;

  createEvent(event: Omit<Event, 'id'>): Promise<Event>;

  updateEvent(id: number, event: Partial<Omit<Event, 'id'>>): Promise<Event>;

  addVote(
    ideaId: number,
    voterId: string,
    expertVoterIds?: string[],
  ): Promise<{
    status: string;
    data?: { totalVotes: number; expertVotes?: number };
  }>;

  addExpertVote(
    ideaId: number,
    voterId: string,
    expertVoterIds?: string[],
  ): Promise<{
    status: string;
    data?: { totalVotes: number; expertVotes?: number };
  }>;

  getUserVotes(voterId: string): Promise<any>;

  getUserVoteCount(voterId: string): Promise<any>;

  getIdeaVotes(ideaId: number): Promise<any>;

  getIdeaVoteCount(
    ideaId: number | undefined,
    bypassCache: boolean,
  ): Promise<any>;

  getIdeaExpertVoteCount(
    ideaId: number,
    expertVoterIds: string[],
  ): Promise<any>;

  removeVote(
    ideaId: number,
    voterId: string,
    expertVoterIds?: string[],
  ): Promise<{
    status: string;
    data?: { totalVotes: number; expertVotes?: number };
  }>;

  removeExpertVote(
    ideaId: number,
    voterId: string,
    expertVoterIds?: string[],
  ): Promise<{
    status: string;
    data?: { totalVotes: number; expertVotes?: number };
  }>;

  getJuryMembers(): Promise<any>;

  getJuryMemberUserRef(userRef: string): Promise<any>;

  addJuryMember(juryMember: {
    userRef: string;
    active: boolean;
    canVote: boolean;
  }): Promise<any>;

  updateJuryMember(
    userRef: string,
    juryMember: { userRef: string; active: boolean; canVote: boolean },
  ): Promise<any>;

  deleteJuryMember(userRef: string): Promise<any>;

  toggleJuryMemberIsActive(userRef: string, isActive: boolean): Promise<any>;

  toggleJuryMemberCanVote(userRef: string, canVote: boolean): Promise<any>;

  listEventConfigValues(eventId: number): Promise<EventConfigValue[]>;

  getConfigValueForEvent(
    configKey: string,
    eventId: number,
  ): Promise<EventConfigValue>;

  updateEventConfigValue(
    eventId: number,
    configKey: string,
    metadata: object,
  ): Promise<void>;
}

/** @public */
export const isTomtomLabAvailable = async (
  entity: Entity,
  context: { apis: ApiHolder },
): Promise<boolean> => {
  const tomtomLabClient = context.apis.get(tomtomLabApiRef);
  if (tomtomLabClient === undefined) {
    return false;
  }
  const entityRef = stringifyEntityRef({
    kind: entity.kind,
    name: entity.metadata.name,
    namespace: entity.metadata.namespace,
  });
  const response = await tomtomLabClient.getIdeasByMemberUserRef(entityRef);
  const idea = await response.json();
  return idea.data.length > 0;
};

export class TomtomLabClient implements TomtomLabApi {
  private readonly identityApi: IdentityApi;
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly baseUrl: Promise<string>;

  constructor(options: {
    identityApi: IdentityApi;
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
  }) {
    this.identityApi = options.identityApi;
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.baseUrl = this.getBaseUrl();
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('tomtom-lab');
  }

  private getDefaultHeaders(): Record<string, string> {
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...getLocalDevUserHeaders(),
    };
  }

  async updateIdea(idea: any): Promise<any> {
    const baseUrl = await this.baseUrl;

    return await this.fetchApi
      .fetch(`${baseUrl}/ideas`, {
        method: 'PUT',
        headers: this.getDefaultHeaders(),
        body: JSON.stringify(idea),
      })
      .then(resp => resp.json());
  }

  async likeIdea(ideaId: number): Promise<IdeaLikes> {
    return this.setIdeaLike(ideaId, 'like');
  }

  async unlikeIdea(ideaId: number): Promise<IdeaLikes> {
    return this.setIdeaLike(ideaId, 'unlike');
  }

  private async setIdeaLike(
    ideaId: number,
    action: 'like' | 'unlike',
  ): Promise<IdeaLikes> {
    const baseUrl = await this.baseUrl;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/ideas/${encodeURIComponent(ideaId)}/${action}`,
      {
        method: 'POST',
        headers: this.getDefaultHeaders(),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    const payload = await response.json();
    return payload.data;
  }

  async getIdeaComments(ideaId: number): Promise<IdeaComment[]> {
    const baseUrl = await this.baseUrl;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/idea-comments/idea/${encodeURIComponent(ideaId)}`,
      {
        method: 'GET',
        headers: this.getDefaultHeaders(),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    const payload = await response.json();
    return payload.data ?? [];
  }

  async addIdeaComment(ideaId: number, comment: string): Promise<IdeaComment> {
    const baseUrl = await this.baseUrl;

    const response = await this.fetchApi.fetch(`${baseUrl}/idea-comments`, {
      method: 'POST',
      headers: this.getDefaultHeaders(),
      body: JSON.stringify({ ideaId, comment }),
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    const payload = await response.json();
    return payload.data;
  }

  async updateIdeaComment(
    commentId: number,
    comment: string,
  ): Promise<IdeaComment> {
    const baseUrl = await this.baseUrl;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/idea-comments/${encodeURIComponent(commentId)}`,
      {
        method: 'PUT',
        headers: this.getDefaultHeaders(),
        body: JSON.stringify({ comment }),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    const payload = await response.json();
    return payload.data;
  }

  async deleteIdeaComment(commentId: number): Promise<void> {
    const baseUrl = await this.baseUrl;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/idea-comments/${encodeURIComponent(commentId)}`,
      {
        method: 'DELETE',
        headers: this.getDefaultHeaders(),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
  }

  async registerIdea(idea: any): Promise<any> {
    const baseUrl = await this.baseUrl;

    return await this.fetchApi
      .fetch(`${baseUrl}/ideas`, {
        method: 'POST',
        headers: this.getDefaultHeaders(),
        body: JSON.stringify(idea),
      })
      .then(resp => resp.json());
  }

  async getIdeaById(id: number): Promise<any> {
    const baseUrl = await this.baseUrl;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/ideas/${encodeURIComponent(id)}`,
    );

    return response.ok ? response : null;
  }

  async getIdeasByMemberUserRef(entityRef: string): Promise<any> {
    const baseUrl = await this.baseUrl;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/ideas/${encodeURIComponent(entityRef)}`,
    );

    if (response.ok) {
      const payload = await response.json();
      return payload.data;
    }
    return null;
  }

  async getMembers(id: number): Promise<any> {
    const baseUrl = await this.baseUrl;

    return await this.fetchApi
      .fetch(`${baseUrl}/ideas/${encodeURIComponent(id)}/members`)
      .then(resp => resp.json());
  }

  async addMember(id: number, userId: string): Promise<void> {
    const baseUrl = await this.baseUrl;
    const { picture } = await this.identityApi.getProfileInfo();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/ideas/${encodeURIComponent(id)}/member/${encodeURIComponent(
        userId,
      )}`,
      {
        method: 'PUT',
        headers: this.getDefaultHeaders(),
        body: JSON.stringify({ picture }),
      },
    );

    if (response.status === 400) {
      return await response.json();
    }

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return await response.json();
  }

  async deleteMember(id: number, userId: string): Promise<any> {
    const baseUrl = await this.baseUrl;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/ideas/${encodeURIComponent(id)}/member/${encodeURIComponent(
        userId,
      )}`,
      {
        method: 'DELETE',
        headers: this.getDefaultHeaders(),
      },
    );

    if (response.status === 400) {
      return await response.json();
    }

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return await response.json();
  }

  async getIdeas(
    limit?: number,
    order?: string,
    eventId?: number,
  ): Promise<any> {
    const baseUrl = await this.baseUrl;
    const params = {
      ...(limit ? { limit: limit.toString() } : {}),
      ...(order ? { order } : {}),
      ...(eventId ? { eventId: eventId.toString() } : {}),
    };
    const query = new URLSearchParams(params);
    const url = `ideas?${query.toString()}`;

    const data = await this.fetchApi.fetch(`${baseUrl}/${url}`);
    if (!data.ok) {
      throw await ResponseError.fromResponse(data);
    }
    return data.json();
  }

  async deleteIdea(id: number): Promise<void> {
    const baseUrl = await this.baseUrl;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/ideas/${encodeURIComponent(id)}`,
      {
        method: 'DELETE',
        headers: this.getDefaultHeaders(),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
  }

  async submitSolution(solution: any): Promise<any> {
    const baseUrl = await this.baseUrl;

    const data = await this.fetchApi.fetch(
      `${baseUrl}/ideas/${solution.ideaId}/solutions`,
      {
        method: 'POST',
        headers: this.getDefaultHeaders(),
        body: JSON.stringify(solution),
      },
    );

    if (!data.ok) {
      throw await ResponseError.fromResponse(data);
    }
    return data.json();
  }

  async updateAssessment(assessment: Assessment): Promise<any> {
    const baseUrl = await this.baseUrl;

    const data = await this.fetchApi.fetch(`${baseUrl}/assessments`, {
      method: 'PUT',
      headers: this.getDefaultHeaders(),
      body: JSON.stringify(assessment),
    });

    if (!data.ok) {
      throw await ResponseError.fromResponse(data);
    }
    return data.json();
  }

  async createAssessment(assessment: Assessment): Promise<any> {
    const baseUrl = await this.baseUrl;

    const data = await this.fetchApi.fetch(`${baseUrl}/assessments`, {
      method: 'POST',
      headers: this.getDefaultHeaders(),
      body: JSON.stringify(assessment),
    });

    if (!data.ok) {
      throw await ResponseError.fromResponse(data);
    }
    return data.json();
  }

  async getSelfAssessment(ideaId: number): Promise<any> {
    const baseUrl = await this.baseUrl;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/assessments/${ideaId}`,
      {
        method: 'GET',
        headers: this.getDefaultHeaders(),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const payload = await response.json();
    return payload.data;
  }

  async getActiveEventParticipants(): Promise<ReadonlyArray<Participant>> {
    const baseUrl = await this.baseUrl;
    const response = await this.fetchApi.fetch(`${baseUrl}/participants`, {
      method: 'GET',
      headers: this.getDefaultHeaders(),
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const payload = await response.json();
    return payload.data;
  }

  async getJuryAssessment(ideaId: number, userRef: string): Promise<any> {
    const baseUrl = await this.baseUrl;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/assessments/${ideaId}/${encodeURIComponent(userRef)}`,
      {
        method: 'GET',
        headers: this.getDefaultHeaders(),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const payload = await response.json();
    return payload.data;
  }

  async getJuryAssessmentsForIdea(ideaId: number): Promise<Assessment[]> {
    const baseUrl = await this.baseUrl;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/assessments/jury/scores/${ideaId}`,
      {
        method: 'GET',
        headers: this.getDefaultHeaders(),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const payload = await response.json();
    return payload.data;
  }

  async registerParticipant(participant: Participant): Promise<any> {
    const baseUrl = await this.discoveryApi.getBaseUrl('tomtom-lab');

    return await this.fetchApi
      .fetch(`${baseUrl}/participants`, {
        method: 'POST',
        headers: this.getDefaultHeaders(),
        body: JSON.stringify(participant),
      })
      .then(resp => resp.json());
  }

  async getActiveEventParticipant(
    userRef: string,
  ): Promise<Readonly<Participant> | null> {
    const baseUrl = await this.baseUrl;
    const data = await this.fetchApi.fetch(
      `${baseUrl}/participants/${encodeURIComponent(userRef)}`,
      {
        method: 'GET',
        headers: this.getDefaultHeaders(),
      },
    );

    if (!data.ok) {
      return null;
    }

    const body = await data.json();
    return body.data;
  }

  async updateParticipant(
    participant: Participant,
  ): Promise<{ status: string }> {
    const baseUrl = await this.baseUrl;
    return this.fetchApi
      .fetch(
        `${baseUrl}/participants/${encodeURIComponent(participant.userRef)}`,
        {
          method: 'PUT',
          headers: this.getDefaultHeaders(),
          body: JSON.stringify(participant),
        },
      )
      .then(resp => resp.json());
  }

  async getEvents(filter: ListEventFilter): Promise<Event[]> {
    const baseUrl = await this.baseUrl;
    const params = new URLSearchParams(filter as Record<string, string>);
    const response = await this.fetchApi.fetch(
      `${baseUrl}/events?${params.toString()}`,
      {
        method: 'GET',
        headers: this.getDefaultHeaders(),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const payload = await response.json();
    return payload.data;
  }

  async getEventById(id: number): Promise<Event | null> {
    const baseUrl = await this.baseUrl;
    const response = await this.fetchApi.fetch(`${baseUrl}/events/${id}`, {
      method: 'GET',
      headers: this.getDefaultHeaders(),
    });

    if (!response.ok) {
      return null;
    }

    const payload = await response.json();
    return payload.data;
  }

  async createEvent(event: Omit<Event, 'id'>): Promise<Event> {
    const baseUrl = await this.baseUrl;
    const response = await this.fetchApi.fetch(`${baseUrl}/events`, {
      method: 'POST',
      headers: this.getDefaultHeaders(),
      body: JSON.stringify(event),
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    const payload = await response.json();
    return payload.data;
  }

  async updateEvent(
    id: number,
    event: Partial<Omit<Event, 'id'>>,
  ): Promise<Event> {
    const baseUrl = await this.baseUrl;
    const response = await this.fetchApi.fetch(`${baseUrl}/events/${id}`, {
      method: 'PUT',
      headers: this.getDefaultHeaders(),
      body: JSON.stringify(event),
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    const payload = await response.json();
    return payload.data;
  }

  async addVote(
    ideaId: number,
    voterId: string,
    expertVoterIds?: string[],
  ): Promise<{
    status: string;
    data?: { totalVotes: number; expertVotes?: number };
  }> {
    const baseUrl = await this.baseUrl;

    const response = await this.fetchApi.fetch(`${baseUrl}/idea-votes/add`, {
      method: 'POST',
      headers: this.getDefaultHeaders(),
      body: JSON.stringify({ ideaId, voterId, expertVoterIds }),
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.json();
  }

  async addExpertVote(
    ideaId: number,
    voterId: string,
    expertVoterIds?: string[],
  ): Promise<{
    status: string;
    data?: { totalVotes: number; expertVotes?: number };
  }> {
    const baseUrl = await this.baseUrl;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/idea-votes/experts/add`,
      {
        method: 'POST',
        headers: this.getDefaultHeaders(),
        body: JSON.stringify({ ideaId, voterId, expertVoterIds }),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.json();
  }

  async getUserVotes(voterId: string): Promise<any> {
    const baseUrl = await this.baseUrl;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/idea-votes/users/${encodeURIComponent(voterId)}`,
      {
        method: 'GET',
        headers: this.getDefaultHeaders(),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.json();
  }

  async getUserVoteCount(voterId: string): Promise<any> {
    const baseUrl = await this.baseUrl;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/idea-votes/users/${encodeURIComponent(voterId)}/count`,
      {
        method: 'GET',
        headers: this.getDefaultHeaders(),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.json();
  }

  async getIdeaVotes(ideaId: number): Promise<any> {
    const baseUrl = await this.baseUrl;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/idea-votes/ideas/${encodeURIComponent(ideaId)}`,
      {
        method: 'GET',
        headers: this.getDefaultHeaders(),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.json();
  }

  async getIdeaVoteCount(ideaId: number, bypassCache: boolean): Promise<any> {
    const baseUrl = await this.baseUrl;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/idea-votes/ideas/${encodeURIComponent(ideaId)}/count`,
      {
        method: 'GET',
        headers: this.getDefaultHeaders(),
        cache: bypassCache ? 'reload' : 'default',
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.json();
  }

  async getIdeaExpertVoteCount(
    ideaId: number,
    expertVoterIds: string[],
  ): Promise<any> {
    const baseUrl = await this.baseUrl;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/idea-votes/ideas/${encodeURIComponent(ideaId)}/expert-count`,
      {
        method: 'POST',
        headers: this.getDefaultHeaders(),
        body: JSON.stringify({ expertVoterIds }),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.json();
  }

  async removeVote(
    ideaId: number,
    voterId: string,
    expertVoterIds?: string[],
  ): Promise<{
    status: string;
    data?: { totalVotes: number; expertVotes?: number };
  }> {
    const baseUrl = await this.baseUrl;

    const response = await this.fetchApi.fetch(`${baseUrl}/idea-votes/remove`, {
      method: 'DELETE',
      headers: this.getDefaultHeaders(),
      body: JSON.stringify({ ideaId, voterId, expertVoterIds }),
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.json();
  }

  async removeExpertVote(
    ideaId: number,
    voterId: string,
    expertVoterIds?: string[],
  ): Promise<{
    status: string;
    data?: { totalVotes: number; expertVotes?: number };
  }> {
    const baseUrl = await this.baseUrl;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/idea-votes/experts/remove`,
      {
        method: 'DELETE',
        headers: this.getDefaultHeaders(),
        body: JSON.stringify({ ideaId, voterId, expertVoterIds }),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.json();
  }

  async getJuryMembers(): Promise<any> {
    const baseUrl = await this.baseUrl;
    const response = await this.fetchApi.fetch(`${baseUrl}/jury/members`, {
      method: 'GET',
      headers: this.getDefaultHeaders(),
    });
    if (!response.ok) throw await ResponseError.fromResponse(response);
    return (await response.json()).data;
  }

  async getJuryMemberUserRef(userRef: string): Promise<any> {
    const baseUrl = await this.baseUrl;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/jury/members/${encodeURIComponent(userRef)}`,
      {
        method: 'GET',
        headers: this.getDefaultHeaders(),
      },
    );
    if (!response.ok) throw await ResponseError.fromResponse(response);
    return (await response.json()).data;
  }

  async addJuryMember(juryMember: {
    userRef: string;
    active: boolean;
    canVote: boolean;
  }): Promise<any> {
    const baseUrl = await this.baseUrl;
    const response = await this.fetchApi.fetch(`${baseUrl}/jury/members`, {
      method: 'POST',
      headers: this.getDefaultHeaders(),
      body: JSON.stringify(juryMember),
    });
    if (!response.ok) throw await ResponseError.fromResponse(response);
    return (await response.json()).data;
  }

  async updateJuryMember(
    userRef: string,
    juryMember: { userRef: string; active: boolean; canVote: boolean },
  ): Promise<any> {
    const baseUrl = await this.baseUrl;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/jury/members/${encodeURIComponent(userRef)}`,
      {
        method: 'PUT',
        headers: this.getDefaultHeaders(),
        body: JSON.stringify(juryMember),
      },
    );
    if (!response.ok) throw await ResponseError.fromResponse(response);
    return (await response.json()).data;
  }

  async deleteJuryMember(userRef: string): Promise<any> {
    const baseUrl = await this.baseUrl;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/jury/members/${encodeURIComponent(userRef)}`,
      {
        method: 'DELETE',
        headers: this.getDefaultHeaders(),
      },
    );
    if (!response.ok) throw await ResponseError.fromResponse(response);
    return (await response.json()).data;
  }

  async toggleJuryMemberIsActive(
    userRef: string,
    isActive: boolean,
  ): Promise<any> {
    const baseUrl = await this.baseUrl;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/jury/members/${encodeURIComponent(userRef)}/toggle-active`,
      {
        method: 'POST',
        headers: this.getDefaultHeaders(),
        body: JSON.stringify({ active: isActive }),
      },
    );
    if (!response.ok) throw await ResponseError.fromResponse(response);
    return (await response.json()).data;
  }

  async toggleJuryMemberCanVote(
    userRef: string,
    canVote: boolean,
  ): Promise<any> {
    const baseUrl = await this.baseUrl;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/jury/members/${encodeURIComponent(userRef)}/toggle-can-vote`,
      {
        method: 'POST',
        headers: this.getDefaultHeaders(),
        body: JSON.stringify({ canVote }),
      },
    );
    if (!response.ok) throw await ResponseError.fromResponse(response);
    return (await response.json()).data;
  }

  async listEventConfigValues(eventId: number): Promise<EventConfigValue[]> {
    const baseUrl = await this.baseUrl;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/config/${eventId}/configs`,
      {
        method: 'GET',
        headers: this.getDefaultHeaders(),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const payload = await response.json();
    return payload.data;
  }

  async getConfigValueForEvent(
    configKey: string,
    eventId: number,
  ): Promise<EventConfigValue> {
    const baseUrl = await this.baseUrl;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/config/${eventId}/configs/${configKey}`,
      {
        method: 'GET',
        headers: this.getDefaultHeaders(),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const payload = await response.json();
    return payload.data;
  }

  async updateEventConfigValue(
    eventId: number,
    configKey: string,
    metadata: object,
  ): Promise<void> {
    const baseUrl = await this.baseUrl;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/config/${eventId}/configs/${configKey}`,
      {
        method: 'PUT',
        headers: this.getDefaultHeaders(),
        body: JSON.stringify(metadata),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
  }
}
